import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Play, Lock, CheckCircle2, Clock, BookOpen, 
  ChevronRight, Sparkles, ArrowRight, Flower2, MessageCircle, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import MasterclassCard, { LimitesUpsellCard } from '../components/MasterclassCard';
import RetreatCard from '../components/RetreatCard';

// Optimize Cloudinary URLs with auto-format and quality
const optimizeImg = (url, width) => {
  if (!url) return url;
  if (url.includes('res.cloudinary.com') && !url.includes('f_auto')) {
    return url.replace('/upload/', `/upload/f_auto,q_auto${width ? `,w_${width}` : ''}/`);
  }
  return url;
};
const FORMATION_WEIGHT = {
  'hieros-gamos': 100,
  'facilitadoras-sosten-energetico': 90,
  'limites': 70,
};

// Brand colors per formation (from their landing pages)
const FORMATION_COLORS = {
  'facilitadoras-sosten-energetico': { primary: '#8a5a7a', light: '#8a5a7a20', text: '#8a5a7a' },
  'limites': { primary: '#c9a227', light: '#c9a22720', text: '#8a7428' },
  'hieros-gamos': { primary: '#D4A84B', light: '#D4A84B20', text: '#D4A84B' },
};
const DEFAULT_COLORS = { primary: '#D4A84B', light: '#D4A84B20', text: '#D4A84B' };

const getFormationWeight = (slug) => FORMATION_WEIGHT[slug] || 50;
const getFormationColors = (slug) => FORMATION_COLORS[slug] || DEFAULT_COLORS;

// Determine the status of a formation node
const getNodeStatus = (enrollment) => {
  const courseAccess = enrollment.course?.course_access_enabled;
  const isActive = enrollment.status === 'active';
  const progress = enrollment.progress || 0;

  // Completed: progress is 100% or has specific flag
  if (progress >= 100 || enrollment.is_completed) return 'completed';
  // Active: enrolled, access is enabled
  if (isActive && courseAccess) return 'active';
  // Upcoming: enrolled but course not yet enabled
  if (isActive && !courseAccess) return 'upcoming';
  // Pending: payment pending
  if (enrollment.status === 'pending_deposit' || enrollment.status === 'pending_subscription') return 'pending';
  // Default
  return 'upcoming';
};

// Path Node Component
const PathNode = ({ enrollment, index, total, isDesktop }) => {
  const status = getNodeStatus(enrollment);
  const courseSlug = enrollment.course?.slug || enrollment.course_slug;
  const progress = enrollment.progress || 0;
  const title = enrollment.course?.title || 'Formación';
  const brandColors = getFormationColors(courseSlug);

  // Short title for the node - use full title for better readability
  const shortTitle = title;

  const canAccess = status === 'completed' || status === 'active';

  const NodeContent = () => (
    <div className="flex flex-col items-center gap-2 group" data-testid={`path-node-${index}`}>
      {/* Node circle */}
      <div
        className={`relative w-14 h-14 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${canAccess ? 'group-hover:scale-110' : ''}`}
        style={{
          borderColor: status === 'completed' ? brandColors.primary : status === 'active' ? brandColors.primary : '#e5e7eb',
          backgroundColor: status === 'completed' ? brandColors.light : status === 'active' ? brandColors.light : '#f9fafb',
          boxShadow: status === 'active' ? `0 10px 15px -3px ${brandColors.primary}30` : status === 'completed' ? `0 10px 15px -3px ${brandColors.primary}20` : 'none',
        }}
      >
        {status === 'completed' ? (
          <CheckCircle2 className="w-6 h-6" style={{ color: brandColors.primary }} />
        ) : status === 'active' ? (
          <div className="relative w-10 h-10">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15" fill="none" stroke="#f3f4f6" strokeWidth="2.5" />
              <circle cx="18" cy="18" r="15" fill="none" stroke={brandColors.primary} strokeWidth="2.5"
                strokeDasharray={`${progress * 0.94} 100`}
                strokeLinecap="round" className="transition-all duration-700" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold" style={{ color: brandColors.primary }}>
              {progress}%
            </span>
          </div>
        ) : status === 'upcoming' ? (
          <Clock className="w-5 h-5 text-gray-300" />
        ) : (
          <Lock className="w-5 h-5 text-gray-300" />
        )}

        {/* Active pulse */}
        {status === 'active' && (
          <div className="absolute inset-0 rounded-full border-2 animate-ping" style={{ borderColor: `${brandColors.primary}40`, animationDuration: '2s' }} />
        )}
      </div>

      {/* Title */}
      <span
        className="text-xs font-medium text-center max-w-[160px] leading-tight"
        style={{ color: status === 'completed' || status === 'active' ? brandColors.text : '#9ca3af' }}
      >
        {shortTitle}
      </span>

      {/* Status label */}
      {status === 'completed' && (
        <span className="text-[10px] font-medium" style={{ color: brandColors.primary }}>Integrado</span>
      )}
      {status === 'upcoming' && (
        <span className="text-[10px] text-gray-400">Próximamente</span>
      )}
    </div>
  );

  if (canAccess) {
    return (
      <Link to={`/formaciones/${courseSlug}/curso`} className="no-underline">
        <NodeContent />
      </Link>
    );
  }

  return <NodeContent />;
};

// Golden Path Component
const GoldenPath = ({ enrollments }) => {
  if (!enrollments.length) return null;

  return (
    <div className="mb-10" data-testid="golden-path">
      {/* Section label */}
      <div className="flex items-center gap-2 mb-6">
        <div className="w-8 h-[1px] bg-[#D4A84B]/40" />
        <span className="text-xs uppercase tracking-widest text-[#D4A84B]/60 font-medium">Tu recorrido</span>
        <div className="flex-1 h-[1px] bg-[#D4A84B]/20" />
      </div>

      {/* Desktop: horizontal path */}
      <div className="hidden md:flex items-start justify-center gap-0 relative py-4">
        {enrollments.map((enrollment, i) => (
          <React.Fragment key={enrollment.id}>
            <PathNode enrollment={enrollment} index={i} total={enrollments.length} isDesktop />
            {/* Connector line */}
            {i < enrollments.length - 1 && (
              <div className="flex items-center mt-5 mx-2">
                <div
                  className="w-16 lg:w-24 h-[2px] rounded-full"
                  style={{
                    background: getNodeStatus(enrollments[i]) === 'completed' || getNodeStatus(enrollments[i]) === 'active'
                      ? `linear-gradient(to right, ${getFormationColors(enrollments[i].course?.slug).primary}, ${
                          getNodeStatus(enrollments[i + 1]) !== 'upcoming' && getNodeStatus(enrollments[i + 1]) !== 'pending'
                            ? getFormationColors(enrollments[i + 1].course?.slug).primary
                            : '#e5e7eb'
                        })`
                      : '#e5e7eb'
                  }}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile: vertical path */}
      <div className="md:hidden flex flex-col items-center gap-0 py-2">
        {enrollments.map((enrollment, i) => (
          <React.Fragment key={enrollment.id}>
            <PathNode enrollment={enrollment} index={i} total={enrollments.length} />
            {/* Vertical connector */}
            {i < enrollments.length - 1 && (
              <div
                className="w-[2px] h-8 rounded-full my-1"
                style={{
                  background: getNodeStatus(enrollments[i]) === 'completed' || getNodeStatus(enrollments[i]) === 'active'
                    ? `linear-gradient(to bottom, ${getFormationColors(enrollments[i].course?.slug).primary}, #e5e7eb)`
                    : '#e5e7eb'
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Unified Formation Card — same size for all statuses
const FormationCard = ({ enrollment }) => {
  const status = getNodeStatus(enrollment);
  const courseSlug = enrollment.course?.slug || enrollment.course_slug;
  const progress = enrollment.progress || 0;
  const modulesCount = enrollment.unlocked_modules?.length || 0;
  const brandColors = getFormationColors(courseSlug);
  const canAccess = status === 'completed' || status === 'active';
  const isUpcoming = status === 'upcoming' || status === 'pending';

  const statusLabel = {
    completed: { text: 'Integrado', icon: CheckCircle2 },
    active: { text: 'Formación activa', icon: Sparkles },
    upcoming: { text: 'Próximamente', icon: Clock },
    pending: { text: 'Pendiente', icon: Clock },
  }[status] || { text: '', icon: Clock };
  const StatusIcon = statusLabel.icon;

  const cardContent = (
    <div
      className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 group ${canAccess ? 'hover:shadow-lg hover:-translate-y-1 cursor-pointer' : ''}`}
      style={{ borderWidth: '1px', borderColor: isUpcoming ? '#e5e7eb' : `${brandColors.primary}25` }}
      data-testid={`formation-card-${courseSlug}`}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="sm:w-48 h-40 sm:h-auto flex-shrink-0 relative overflow-hidden">
          <img
            src={optimizeImg(enrollment.course?.hero_image_url, 600) || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=600&q=80'}
            alt={enrollment.course?.title}
            loading="lazy"
            className={`w-full h-full object-cover transition-transform duration-500 ${canAccess ? 'group-hover:scale-105' : ''} ${isUpcoming ? 'opacity-50' : ''}`}
          />
          {isUpcoming && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/5">
              <Clock className="w-8 h-8 text-white/70" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center">
          {/* Status badge */}
          <div className="flex items-center gap-2 mb-3">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: isUpcoming ? '#f3f4f6' : `${brandColors.primary}15`,
                color: isUpcoming ? '#9ca3af' : brandColors.primary,
              }}
            >
              <StatusIcon className="w-3 h-3" />
              {statusLabel.text}
            </span>
          </div>

          {/* Title */}
          <h3 className={`text-lg sm:text-xl font-heading mb-2 ${isUpcoming ? 'text-gray-400' : 'text-gray-800'}`}>
            {enrollment.course?.title}
          </h3>

          {/* Info */}
          {!isUpcoming && (
            <>
              <p className="text-gray-400 text-sm mb-4">
                {modulesCount} módulo{modulesCount !== 1 ? 's' : ''} disponible{modulesCount !== 1 ? 's' : ''}
              </p>

              {/* Progress bar */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${progress}%`, backgroundColor: brandColors.primary }}
                  />
                </div>
                <span className="text-sm font-semibold" style={{ color: brandColors.primary }}>{progress}%</span>
              </div>

              {/* Button */}
              <div>
                <span
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-medium transition-all"
                  style={{ backgroundColor: brandColors.primary }}
                >
                  {status === 'completed' ? (
                    <>
                      <BookOpen className="w-4 h-4" />
                      Revisar contenido
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Continuar formación
                    </>
                  )}
                </span>
              </div>
            </>
          )}

          {isUpcoming && (
            <p className="text-gray-400 text-sm">
              Te avisaremos cuando esté disponible.
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (canAccess) {
    return (
      <Link to={`/formaciones/${courseSlug}/curso`} className="block no-underline">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};

// Library Section for recorded courses
const BibliotecaSection = ({ enrollments }) => {
  if (!enrollments.length) return null;

  return (
    <section className="mt-12" data-testid="biblioteca-section">
      <div className="flex items-center gap-2 mb-6">
        <BookOpen className="w-5 h-5 text-[#D4A84B]/60" strokeWidth={1.5} />
        <h2 className="text-xl font-heading text-gray-800">Biblioteca</h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {enrollments.map((enrollment, index) => (
          <Link
            key={enrollment.id}
            to={`/curso/${enrollment.course_id}`}
            className="group"
            data-testid={`library-course-${index}`}
          >
            <div className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-1">
              <div className="h-36 overflow-hidden">
                <img
                  src={optimizeImg(enrollment.course?.image_url, 400) || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=600&q=80'}
                  alt={enrollment.course?.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <h3 className="font-medium text-gray-800 mb-2 group-hover:text-[#D4A84B] transition-colors">
                  {enrollment.course?.title}
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Progress value={enrollment.progress || 0} className="h-1.5" />
                  </div>
                  <span className="text-xs text-gray-400 font-medium">{enrollment.progress || 0}%</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

// Main Page
const MiCamino = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [specialEnrollments, setSpecialEnrollments] = useState([]);
  const [regularEnrollments, setRegularEnrollments] = useState([]);
  const [campusAccess, setCampusAccess] = useState(false);
  const [campusCardVisible, setCampusCardVisible] = useState(true);
  const [masterclassData, setMasterclassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoadError(false);
      try {
        // If we arrive via welcome=masterclass&mc=<slug>, prefer fetching THAT
        // specific masterclass (so the WhatsApp URL and title match the one the
        // user just registered for, even if there are several published).
        const params = new URLSearchParams(window.location.search);
        const mcSlug = params.get('mc');
        const masterclassPromise = mcSlug
          ? api.get(`/masterclasses/${mcSlug}/me`)
              .then(r => ({ data: {
                config: { ...(r.data.masterclass || {}), is_published: (r.data.masterclass?.status === 'published') },
                is_enrolled: !!r.data.is_enrolled,
                enrollment: r.data.enrollment,
                recording_available_now: !!r.data.masterclass?.recording_available_now,
              }}))
              .catch(() => api.get('/masterclass/me').catch(() => ({ data: null })))
          : api.get('/masterclass/me').catch(() => ({ data: null }));
        const [specialRes, regularRes, masterclassRes] = await Promise.all([
          api.get('/my-special-enrollments'),
          api.get('/enrollments'),
          masterclassPromise,
        ]);
        setSpecialEnrollments(specialRes.data || []);
        setRegularEnrollments(regularRes.data || []);
        setMasterclassData(masterclassRes.data);
        api.get('/campus/status').then(res => setCampusAccess(res.data.has_access)).catch(() => {});
        api.get('/campus/config').then(res => setCampusCardVisible(res.data?.mi_camino_card_visible !== false)).catch(() => {});
      } catch (error) {
        console.error('Error fetching enrollments:', error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const reloadMasterclass = () => {
    api.get('/masterclass/me').then(res => setMasterclassData(res.data)).catch(() => {});
  };

  const hasLimites = useMemo(
    () => specialEnrollments.some(e => (e.course?.slug || e.course_slug) === 'limites'),
    [specialEnrollments]
  );

  // Sort special enrollments by weight
  const sortedFormations = [...specialEnrollments].sort(
    (a, b) => getFormationWeight(b.course?.slug) - getFormationWeight(a.course?.slug)
  );

  const firstName = user?.name?.split(' ')[0] || 'Alumna';

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 20) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const hasMasterclass = !!masterclassData?.is_enrolled;
  const hasAnyContent = specialEnrollments.length > 0 || regularEnrollments.length > 0 || hasMasterclass;

  // Masterclass welcome banner (shown right after registration/enrollment)
  const searchParams = new URLSearchParams(location.search);
  const welcomeFromMasterclass = searchParams.get('welcome') === 'masterclass';
  const welcomeSlug = searchParams.get('mc');
  const mcConfig = masterclassData?.config;
  const showWelcomeBanner = welcomeFromMasterclass && !welcomeDismissed && mcConfig && hasMasterclass;
  const mcWhatsAppUrl = mcConfig?.whatsapp_channel_url || '';
  const mcSlug = welcomeSlug || mcConfig?.slug || '';

  return (
    <div className="pt-20 min-h-screen bg-[#FDFCFA] pb-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10">

        {/* Welcome */}
        <div className="mb-10">
          <p className="text-[#D4A84B]/70 text-sm font-medium mb-1">{getGreeting()}</p>
          <h1 className="text-3xl lg:text-4xl font-heading text-gray-800 mb-2" data-testid="mi-camino-welcome">
            {firstName}, este es tu camino
          </h1>
          <p className="text-gray-400 text-sm">Tu espacio de integración y aprendizaje consciente.</p>
        </div>

        {/* Masterclass success banner — visible right after registration */}
        {showWelcomeBanner && (
          <div
            className="relative overflow-hidden rounded-2xl border border-[#c9a227]/30 bg-gradient-to-br from-[#fdfbf5] via-[#faf6ed] to-[#f5ecd2] p-6 sm:p-8 mb-8 shadow-sm"
            data-testid="mi-camino-mc-welcome"
          >
            <button
              type="button"
              onClick={() => setWelcomeDismissed(true)}
              className="absolute top-3 right-3 p-1.5 rounded-full text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] hover:bg-white/60 transition-colors"
              aria-label="Cerrar mensaje de bienvenida"
              data-testid="mi-camino-mc-welcome-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-[#c9a227]/15 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#c9a227]" strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-[#8a7428] font-medium mb-1">
                  Tu plaza está reservada
                </p>
                <h2 className="text-xl sm:text-2xl font-heading text-[hsl(var(--text-primary))] leading-snug mb-1">
                  Nos vemos en la masterclass, {firstName}.
                </h2>
                <p className="text-sm text-[hsl(var(--text-secondary))] leading-relaxed">
                  {mcConfig.title}. Recibirás el enlace unas horas antes por email — y aquí, en tu camino, siempre podrás encontrarlo.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 pl-0 sm:pl-[60px]">
              {mcSlug && (
                <Link
                  to={`/masterclass/${mcSlug}`}
                  className="inline-flex items-center gap-2 bg-white/70 hover:bg-white text-[hsl(var(--text-primary))] px-4 py-2 rounded-full text-sm font-medium border border-[#c9a227]/40 transition-colors"
                  data-testid="mi-camino-mc-welcome-view"
                >
                  Ver detalles <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              {mcWhatsAppUrl && (
                <a
                  href={mcWhatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20b658] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors shadow-sm"
                  data-testid="mi-camino-mc-welcome-whatsapp"
                >
                  <MessageCircle className="w-4 h-4" /> Unirme al canal de WhatsApp
                </a>
              )}
            </div>
            {mcWhatsAppUrl && (
              <p className="text-[11px] text-[hsl(var(--text-tertiary))] mt-3 pl-0 sm:pl-[60px]">
                Recibirás recordatorios discretos, el enlace del directo y contenido extra.
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="w-10 h-10 border-2 border-[#D4A84B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Preparando tu espacio...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-8 h-8 text-amber-400" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-heading text-gray-700 mb-3">
              No se pudo cargar tu espacio
            </h2>
            <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
              Puede ser una incidencia puntual. Pulsa para intentarlo de nuevo.
            </p>
            <Button onClick={() => window.location.reload()} className="bg-[#D4A84B] hover:bg-[#c49b3f] text-white px-6 py-3 rounded-xl">
              Reintentar
            </Button>
          </div>
        ) : !hasAnyContent ? (
          /* Empty state — still show the retreat card so newcomers can discover it */
          <>
            <RetreatCard />
            <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
              <div className="w-20 h-20 rounded-full bg-[#D4A84B]/10 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-9 h-9 text-[#D4A84B]" strokeWidth={1.5} />
              </div>
              <h2 className="text-2xl font-heading text-gray-800 mb-3">
                Tu camino está por comenzar
              </h2>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                Explora las formaciones disponibles y da el primer paso en tu proceso de integración.
              </p>
              <Link to="/formaciones" data-testid="explore-formations">
                <Button className="bg-[#D4A84B] hover:bg-[#c49b3f] text-white px-8 py-3 rounded-xl">
                  Explorar formaciones
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Masterclass card — visible para todas mientras esté publicada */}
            <MasterclassCard data={masterclassData} onEnrolled={reloadMasterclass} />

            {/* Retiro presencial — visible para todas */}
            <RetreatCard />

            {/* Campus de Interioridad — visible para todos (configurable desde admin) */}
            {campusCardVisible && (
              <Link to={campusAccess ? "/campus" : "/campus-interioridad"} className="block mb-8 group" data-testid="campus-access-card">
                <div className="relative overflow-hidden rounded-2xl border border-[#c9a227]/30 p-6 sm:p-8 hover:border-[#c9a227]/50 transition-all hover:shadow-lg hover:shadow-[#c9a227]/15"
                  style={{ background: 'linear-gradient(135deg, #c9a227 0%, #a88520 50%, #8b6914 100%)' }}>
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {Array.from({ length: 12 }).map((_, i) => (
                      <div key={i} className="absolute w-px h-px bg-white rounded-full animate-pulse" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.3 + 0.15, animationDelay: `${Math.random() * 3}s` }} />
                    ))}
                  </div>
                  <div className="relative flex items-center gap-5">
                    <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 group-hover:bg-white/25 transition-colors">
                      <Flower2 className="w-7 h-7 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-heading text-white mb-0.5 group-hover:text-white transition-colors">
                        Campus de Interioridad
                      </h3>
                      <p className="text-white/60 text-sm">
                        {campusAccess ? 'Entra a tu espacio sagrado' : 'Descubre tu espacio de consciencia'}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              </Link>
            )}

            {/* The Golden Path */}
            {sortedFormations.length > 0 && (
              <GoldenPath enrollments={sortedFormations} />
            )}

            {/* All Formations — uniform cards */}
            {sortedFormations.length > 0 && (
              <div className="space-y-5 mb-10" data-testid="formations-list">
                {sortedFormations.map(enrollment => (
                  <FormationCard key={enrollment.id} enrollment={enrollment} />
                ))}
              </div>
            )}

            {/* Biblioteca - Recorded Courses */}
            <BibliotecaSection enrollments={regularEnrollments} />

            {/* All completed message */}
            {sortedFormations.length > 0 && sortedFormations.every(e => getNodeStatus(e) === 'completed') && (
              <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-8 text-center">
                <Sparkles className="w-8 h-8 text-[#D4A84B] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-gray-600 font-medium mb-2">Has completado tus formaciones</p>
                <p className="text-gray-400 text-sm mb-6">Explora nuevas posibilidades para seguir expandiendo tu camino.</p>
                <Link to="/formaciones">
                  <Button variant="outline" className="border-[#D4A84B] text-[#D4A84B] hover:bg-[#D4A84B]/10 rounded-xl">
                    Ver formaciones disponibles
                  </Button>
                </Link>
              </div>
            )}
          </>
        )}

        {/* Quote */}
        {hasAnyContent && !loading && (
          <div className="mt-12 text-center py-8">
            <p className="font-accent text-lg italic text-gray-400/80">
              "Cada paso que das hacia ti misma es un regreso a casa."
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiCamino;
